import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import {
  ShieldCheck,
  FolderLock,
  KeyRound,
  Eraser,
  Archive,
  Ban,
  ArrowRight,
  AlertTriangle,
  Fingerprint,
  Aperture,
  CalendarClock,
  Mail,
} from "lucide-react";

/**
 * Public "Trust" page. Marketing-friendly companion to /disclaimer.
 *
 * Purpose: nervous prospects who wonder "is it safe to upload my lease"
 * bounce before they read the full legal disclaimer. This page turns the
 * six data-handling promises into a scannable, iconed grid, with a plain
 * English "why we built it this way" note and an honest "what we can't
 * promise" section so we never oversell the security posture.
 *
 * Everything on this page is a summary of /disclaimer#security-and-data-
 * handling. The full legal text still lives there and is linked here.
 */
export default function Trust() {
  useEffect(() => {
    const prev = document.title;
    document.title = "How TenantTrail keeps your documents safe";
    const description =
      "Plain-English explanation of how TenantTrail stores, encrypts and deletes your tenancy documents. HTTPS, per-user object storage, bcrypt passwords, immediate byte-shredding on delete.";
    let m = document.querySelector('meta[name="description"]');
    const created = !m;
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    const prevContent = m.getAttribute("content");
    m.setAttribute("content", description);
    return () => {
      document.title = prev;
      if (created && m.parentNode) m.parentNode.removeChild(m);
      else if (m && prevContent !== null) m.setAttribute("content", prevContent);
    };
  }, []);

  const promises = [
    {
      key: "transit",
      icon: ShieldCheck,
      title: "Encrypted in transit",
      body:
        "Every request between your browser and TenantTrail goes over HTTPS with TLS. Nothing you upload travels the public internet in cleartext, including on public Wi-Fi.",
    },
    {
      key: "storage",
      icon: FolderLock,
      title: "Namespaced private storage",
      body:
        "Your files live in a private per-user path in our managed object storage. There are no public URLs. Every download is authenticated against your account before any byte leaves the server.",
    },
    {
      key: "password",
      icon: KeyRound,
      title: "Passwords are hashed, not stored",
      body:
        "We store bcrypt hashes with a modern cost factor. Even we cannot read your password. A password reset always issues a fresh token, we never mail you your old password because we do not have it.",
    },
    {
      key: "delete",
      icon: Eraser,
      title: "Delete means delete",
      body:
        "When you delete a document, we immediately overwrite the file in storage with a tombstone so the original content is no longer recoverable, then mark the record as deleted in our database. It is a one-way action, we cannot restore a file you have deleted.",
    },
    {
      key: "backups",
      icon: Archive,
      title: "Backups are encrypted at rest",
      body:
        "Full system backups are encrypted with an AES key that is held separately from the database. If a backup file leaked, it would still be unreadable without the key.",
    },
    {
      key: "no-sale",
      icon: Ban,
      title: "We do not sell your data",
      body:
        "We do not sell, rent or trade your personal information or your documents to third parties, ever. Read our Privacy Policy for the full picture.",
    },
  ];

  const tribunal = [
    {
      key: "fingerprint",
      icon: Fingerprint,
      title: "SHA-256 fingerprint on every upload",
      body:
        "The moment a document reaches us, we compute a SHA-256 hash and store it alongside the file. Change one byte anywhere in the document and the hash no longer matches. It gives you a mathematical way to prove the file has not been altered since you uploaded it.",
    },
    {
      key: "exif",
      icon: Aperture,
      title: "Original photo metadata preserved",
      body:
        "Photos you upload keep their original EXIF data intact: the time, date, camera and (if your device recorded it) location. An agent claiming your inspection photo was taken later, or somewhere else, has to argue against the phone that actually took it.",
    },
    {
      key: "timeline",
      icon: CalendarClock,
      title: "Server-timestamped chronology",
      body:
        "Every timeline entry carries a UTC server timestamp set by us, not by your device. When you lock your chronology ahead of a hearing it becomes read-only. New events can still be added but existing ones cannot be silently edited to change the story.",
    },
    {
      key: "mail",
      icon: Mail,
      title: "Verbatim email capture",
      body:
        "Each case gets its own private reply address. When you correspond with your agent through it, TenantTrail keeps the full message and headers verbatim, so \"I never received that email\" becomes very hard for the other side to argue.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-14 sm:py-20" data-testid="trust-page">

        {/* Hero */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-[#1B6C62]/30 mb-5">
            <ShieldCheck size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
            <span className="label-mono text-[#1B6C62]">Trust and security</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0D2B52] mb-5 leading-[1.1]">
            How we look after your documents
          </h1>
          <p className="text-[#6B7280] max-w-2xl mx-auto leading-relaxed text-lg">
            Your lease, your bond claim, your inspection photos. Uploading them to a website is a leap of faith. Here is the plain-English version of what actually happens to them once you do.
          </p>
        </div>

        {/* Why we built it this way */}
        <div className="max-w-3xl mx-auto bg-[#0D2B52] text-white p-7 sm:p-9 mb-14" data-testid="trust-why">
          <div className="label-mono mb-3 text-white/70">Why we built it this way</div>
          <p className="font-serif text-[16.5px] leading-[1.75]">
            TenantTrail exists because renters are often the smaller party in a dispute. The last thing we want is for our own product to become another power imbalance. So we made choices that put the tenant in control: encrypted transport, per-user storage, hashed passwords and a delete button that actually deletes. If a nervous friend of yours ever asks &ldquo;is it safe to upload my lease?&rdquo;, this page is our answer.
          </p>
        </div>

        {/* Six promises grid */}
        <div className="grid sm:grid-cols-2 gap-6 mb-16" data-testid="trust-promises">
          {promises.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.key}
                className="bg-white border border-[#E5E0D6] p-6 hover:border-[#1B6C62]/40 transition-colors"
                data-testid={`trust-promise-${p.key}`}
              >
                <div className="w-11 h-11 border border-[#1B6C62]/40 bg-[#1B6C62]/5 flex items-center justify-center mb-4">
                  <Icon size={20} strokeWidth={1.75} className="text-[#1B6C62]" />
                </div>
                <h3 className="font-serif text-xl text-[#0D2B52] mb-2 leading-snug">{p.title}</h3>
                <p className="text-[14px] text-[#4a5b6c] leading-[1.65]">{p.body}</p>
              </div>
            );
          })}
        </div>

        {/* Tribunal chain-of-custody */}
        <div className="mb-16" data-testid="trust-tribunal-section">
          <div className="text-center mb-8">
            <div className="label-mono mb-3 text-[#B45309]">Built for the tribunal</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] mb-3 leading-tight">
              Evidence that holds up.
            </h2>
            <p className="text-[#6B7280] max-w-2xl mx-auto leading-relaxed">
              Renters lose disputes for two reasons: they cannot find the evidence when they need it, or the agent claims what they have is fabricated. TenantTrail was built to shut down both.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6" data-testid="trust-tribunal-cards">
            {tribunal.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.key}
                  className="bg-white border border-[#E5E0D6] p-6 hover:border-[#B45309]/40 transition-colors"
                  data-testid={`trust-tribunal-${t.key}`}
                >
                  <div className="w-11 h-11 border border-[#B45309]/40 bg-[#B45309]/5 flex items-center justify-center mb-4">
                    <Icon size={20} strokeWidth={1.75} className="text-[#B45309]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#0D2B52] mb-2 leading-snug">{t.title}</h3>
                  <p className="text-[14px] text-[#4a5b6c] leading-[1.65]">{t.body}</p>
                </div>
              );
            })}
          </div>

          <p className="text-[13px] text-[#6B7280] italic font-serif leading-relaxed max-w-3xl mx-auto mt-6 text-center">
            None of this replaces the tribunal&apos;s own evidence rules. VCAT, QCAT, NCAT and their equivalents each apply their own standards and each decision-maker has discretion. But it means the evidence you bring is dated, verified and harder to dismiss.
          </p>
        </div>

        {/* What we cannot promise (honesty band) */}
        <div className="max-w-3xl mx-auto bg-[#FAF9F6] border-l-4 border-l-[#B45309] px-6 py-5 mb-14" data-testid="trust-cannot-promise">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} strokeWidth={1.75} className="text-[#B45309] mt-1 shrink-0" />
            <div>
              <h2 className="font-serif text-xl text-[#0D2B52] mb-2">What we cannot promise</h2>
              <p className="text-[14.5px] text-[#4a5b6c] leading-[1.7] mb-2">
                No system is 100% secure. No SaaS product on the internet can honestly claim otherwise and we will not pretend to. Attackers evolve, humans make mistakes, third-party dependencies have their own weaknesses.
              </p>
              <p className="text-[14.5px] text-[#4a5b6c] leading-[1.7]">
                Please do not upload anything you would be unwilling to store in a modern cloud service. If your matter involves highly sensitive material (a suppression order, a family violence intervention order, classified information), speak to a solicitor first. If you ever suspect your account has been accessed by someone else, contact us straight away via <Link to="/support" className="text-[#1B6C62] hover:underline">the support page</Link>.
              </p>
            </div>
          </div>
        </div>

        {/* Related links */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <p className="text-[14px] text-[#6B7280] leading-relaxed mb-4">
            The full legal disclaimer, privacy policy and terms of use spell out the details in formal language.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[14px]">
            <Link to="/disclaimer" className="text-[#1B6C62] hover:text-[#0D2B52] underline" data-testid="trust-link-disclaimer">
              Full disclaimer
            </Link>
            <Link to="/privacy" className="text-[#1B6C62] hover:text-[#0D2B52] underline" data-testid="trust-link-privacy">
              Privacy policy
            </Link>
            <Link to="/terms" className="text-[#1B6C62] hover:text-[#0D2B52] underline" data-testid="trust-link-terms">
              Terms of use
            </Link>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-white border border-[#E5E0D6] p-8 sm:p-10 text-center" data-testid="trust-cta">
          <h2 className="font-serif text-3xl text-[#0D2B52] mb-3 leading-tight">
            Ready when you are.
          </h2>
          <p className="text-[#6B7280] mb-6 max-w-xl mx-auto leading-relaxed">
            Start a free case, upload the documents you already have and let TenantTrail keep them organised, timestamped and dispute-ready. No credit card, no upgrade prompts until you actually need one.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-[#1B6C62] hover:brightness-95 text-white px-7 py-2.5 transition-all font-medium"
            data-testid="trust-cta-register"
          >
            Create a free account
            <ArrowRight size={15} strokeWidth={1.75} />
          </Link>
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}
